import { throttle } from 'lodash';
import us from '../../defines';
import { Point } from '../../lib/objects';
import { triggerUScopeEvent } from '../../_uScope';

// ________________________________________________________________________________________________
// statevector.js
//
// 8.11.2020 - CC
// This is a legacy object originally designed to enable testing in uScope and gather performance
// metrics and other misc functions. Over time most of these functions were deprecated/removed; it's
// sole remaining usage is here in the conferencing module. There's some duplication of functionality
// between this and the Viewport's VpView/ViewportState object so this class can probably be removed.
// Having said that, there are some interesting/useful capabilities in here; it's worth exploring
// whether functionality here can be moved to ViewportState.
// /end 8.11.2020 - CC
//
// StateVector object - This is a generic container useful for ...
//    Every item added to the queue is time stamped for time series analyis, playback, etc.
//  There are optional functions onAdd[1,2,..]() that the user can define that will be //cp-not right!!!!!
//        called each time imageUpdate() is called.
//
//    Note that the debug statements are commented outin some functions to reduce vpRefresh time.
// cp-mention that this saves mouse & moves only > a fixed amount...
//
//    history
//    1106    cperz    created
//    _______________________________________end_AIHv1/PR __________________________________________
//
// ________________________________________________________________________________________________
// todo
//    - could make queue trim on time instead of size (with size max still...)
//  - add methods for getlast direction, mouse position, etc....
//            much more straightforward, no need to understand internal storage of this object!
// ________________________________________________________________________________________________
// state types:
//    image: set by viewUpdate()
//    mouse: set by mouseUpdate()
//
us.eStateTypes = {image: 0, mouse: 1}; // TODO: there's only one state type!

const MOUSE_THROTTLE = 50;

export default function StateVector (uScope, viewport) {
  const self = this;
  const imageId_ = viewport.getImageId();
  const uScopeIdx_ = viewport.getImageIdx();

  let imageSizeMax;
  let mouseSizeMax;

  const d = new Date();
  const dTime = 1.0 / 10.0; // get the start time and scale it from millisecs to 1/100th sec
  const startTime = d.getTime() * dTime;

  const imageQueue = []; // the image state vector queue
  const mouseQueue = []; // the mouse state vector queue

  const stateTypeView = us.eStateTypes.view;

  const minMoveMagnitude_ = 12; // the minimum magnitude in pixels of movement to record
  const slopeLimit = 4;
  const slopeLimtInv = 0.25; // 1. / slopeLimit

  const kSVQUEUE_VIEW_SIZE = 500; // number of view states to retain in state vector queue
  // this throttles the responsivity of the state vector!

  // ............................................ _sinceStartTime local
  //    This function is used for all time stamps in this object.
  //    It converts absolute time from "new Date().getTime()" to a differntial in seconds
  //    (to the 3rd decimal place - milliseconds) since the start time of this object.
  function _sinceStartTime (time) {
    return (time * dTime - startTime).toFixed(3);
  }

  // ............................................ _setSize local
  // This function sets the image and mouse queue maximum sizes.
  function _setSize (newSize) {
    const newMax = (newSize) || 200; // maximum size for queue
    if (newMax < 1 || newMax > 2000) { // sanity check on the queue size
      return;
    }

    imageSizeMax = newMax;
    mouseSizeMax = 100; //  parseInt(newMax / 2, 10);     // limit the mouse queue to half the size

    while (imageQueue.length > imageSizeMax) {
      imageQueue.shift();
    }
    while (mouseQueue.length > mouseSizeMax) {
      mouseQueue.shift();
    }
  }

  // ............................................ viewUpdate
  // This function adds an image state vector to its queue.
  // If the state description is a move, the city block distance between this position and the previous state on the queue
  // must be greater than a threshold.  Otherwise the state is not added to the queue.
  //
  // An Image State Vector is a definition of the view state plus:
  //        time stamp
  //        + desc  {"all", "move", "zoom", "focus", "dims", }
  //        + viewport's vpView contents {left, top, width, height, zoom, focus, angleDeg}
  //        + dir {"none", "wholeview", "north", "east", "west", "south", northwest, ...} //cp-fill in and define
  //        + magpx { magnitude of move in pixels }
  //
  //    parameters
  //        state:        {left, top, width, height, zoom, focus, angleDeg}
  //        changeDesc: "all" or "move" or "zoom" or "focus" or "dims" or "rotate"
  //
  //    todo:
  //        - I'd like to know if there are listeners, perhaps I don't do all this if not

  const lastViewState_ = new Point(0, 0);
  this.viewUpdate = function (state, changeDesc) {
    const zoom = state.zoom;

    if (imageQueue.length === imageSizeMax) { // maintain queue size
      imageQueue.shift(); // remove the top element of the array to make room at the bottom
    }

    let magpx = 0;
    if (changeDesc === 'move') { // throttle the responsivity to motion
      magpx = Math.abs(state.left - lastViewState_.x) + Math.abs(state.top - lastViewState_.y); // city-street magnitude of move in pixels
      if (magpx < minMoveMagnitude_) {
        return;
      }
    }

    const sv = []; // add info to new state vector
    sv.time = _sinceStartTime(new Date().getTime()); // add time stamp
    sv.desc = changeDesc; // add input change description
    sv.state = jQuery.extend(true, {}, state); // add input view state, must clone state first
    if (changeDesc === 'move') {
      sv.magpx = magpx; // add magnitude of move (units are pixels)
    }

    _computeDir(sv); // add computed direction 'dir'
    imageQueue.push(sv); // add state vector to the queue
    lastViewState_.set(state.left, state.top); // save last queue view position

    // report coords in normal graphics system space
    const cpCenter = viewport.getViewCenter(us.coordSystemNORM);// report out in normal coordSystem
    triggerUScopeEvent(us.evVIEWCHANGE, { // fire event that the view has changed
      imageId: imageId_,
      uScopeIdx: uScopeIdx_,
      desc: sv.desc,
      x: cpCenter.x,
      y: cpCenter.y,
      zoom: zoom,
      angleDeg: state.angleDeg
    });

    // ............................................ _computeDir
    // This function evaluates the most recent state change and concludes the view motion direction.
    //        It then adds that direction to the passed array.
    function _computeDir (ioSV) {
      ioSV.dir = 'none';
      if (imageQueue.length === 0 || ioSV.desc === 'all' || ioSV.desc !== 'move') {
        return;
      }

      const lastState = getLast(stateTypeView).state;
      const dx = ioSV.state.left - lastState.left;
      const dy = ioSV.state.top - lastState.top;

      if (Math.abs(dx) > lastState.width * 2 || Math.abs(dy) > lastState.height * 2 || // if moved N views  //cp-revisit factor of 2
                ioSV.desc === 'dim') { // or resized view dimensions
        ioSV.dir = 'wholeview'; // then indicate whole view change
        return;
      }
      let dr = slopeLimit + 1; // slope of motion
      if (dy !== 0) {
        dr = dx / dy;
      }

      let dir;
      if (Math.abs(dr) > slopeLimit) { // if horizontal motion
        if (dx < 0) { // if moving left
          dir = 'east';
        } else {
          dir = 'west';
        }
      } else if (Math.abs(dr) < slopeLimtInv) { // if vertical motion
        if (dy < 0) { // if moving up
          dir = 'south';
        } else {
          dir = 'north';
        }
      } else if (dx < 0) { // diagonal motion, moving left
        if (dy < 0) { // if moving up, too
          dir = 'southeast';
        } else {
          dir = 'northeast';
        }
      } else { // diagonal motion, moving right
        if (dy < 0) { // if moving up, too
          dir = 'southwest';
        } else {
          dir = 'northwest';
        }
      }

      ioSV.dir = dir; // add direction to return ioSV
    }
  };

  // ............................................ mouseUpdate
  // This function adds an mouse state vector to its queue.
  // If the state description is a mousemove, the city block distance between this position and the previous state on the queue
  // must be greater than a threshold.  Otherwise the state is not added to the queue.
  //
  //    A Mouse State Vector is a definition of the mouse state:
  //        time stamp
  //        + desc {"mousemove", "mousedown", ...}
  //        + state   Point {x,y} of mouse position in base image coordinates, us.coordSystemNORM
  //
  //    parameters
  //        state:            Point {x,y} of mouse position in base image coordinates
  //        eventDesc:        {"mousemove", "mousedown",}
  //        bForce:            boolean, if true the function does not exit when there are no listeners
  //                                    so us.evMOUSECHANGE event is fired if mouse move is sufficient

  var lastMouseState_ = new Point(0, 0);
  const mouseUpdate = throttle(function mouseUpdate (state, eventDesc) {
    if (mouseQueue.length === mouseSizeMax) { // maintain queue size
      mouseQueue.shift(); // remove the top element of the array to make room at the bottom
    }

    let magpx = 0;
    if (eventDesc === 'mousemove') {
      if (lastMouseState_) { // throttle the responsivity to motion
        magpx = lastMouseState_.cityBlockDistance(state);
        if (magpx < minMoveMagnitude_) {
          return;
        }
      }
    }

    const sv = []; // add info to new state vector
    sv.time = _sinceStartTime(new Date().getTime()); // add time stamp

    sv.desc = eventDesc; // add input change description
    sv.state = jQuery.extend(true, {}, state); // add input view state, must clone state first
    mouseQueue.push(sv); // add state vector to the queue
    lastMouseState_.setPoint(sv.state); // save last queue mouse position

    triggerUScopeEvent(us.evMOUSECHANGE, {
      imageId: imageId_,
      uScopeIdx: viewport.getImageIdx(),
      x: state.x,
      y: state.y
    });
  }, MOUSE_THROTTLE);

  // ............................................ getLast
  // This function returns the last state vector for the specified state type.
  function getLast (stateType) { // cp-todo rename to get LastState
    if (!stateType) {
      stateType = us.eStateTypes.view;
    }
    if (stateType === us.eStateTypes.view) {
      return (imageQueue.length > 0) ? imageQueue[imageQueue.length - 1] : undefined;
    } else {
      return (mouseQueue.length > 0) ? mouseQueue[mouseQueue.length - 1] : undefined;
    }
  }

  // ............................................................................................
  // construction
  _setSize(kSVQUEUE_VIEW_SIZE); // save the max queue size allowed

  viewport.renderer.addLayer({
    updatePosition: () => self.viewUpdate(viewport.getVPView(), 'move'),
    zoomChange: () => self.viewUpdate(viewport.getVPView(), 'zoom'),
    rotateChange: () => self.viewUpdate(viewport.getVPView(), 'rotate')
  });
  viewport.events.on('viewport:image-opened', () => {
    self.viewUpdate(viewport.getVPView(), 'all');
  });
  viewport.vpDivGet().on('mousemove', (e) => {
    if (uScope.isConfLeader()) { // only in conferencing are mouse states recorded
      const cpVpRelPos = us.coordPageToView(e, viewport); // the unrotated viewport relative cursor position
      const basePos = us.coordBaseImage2(cpVpRelPos, viewport); // the cursor in base image coords
      mouseUpdate(basePos, 'mousemove', true); // force a save of this mouse move event
    }
  });
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/conferencing/StateVector.js